import React, { useState } from 'react';
import styled from 'styled-components';
import HeroImage from './HeroImage';
import HeroDataModule from '../models/HeroData';
import { getMatch } from '../services/OpenDotaMatchCache';
import { extractPickInsight } from '../utils/openDotaPickInsight';
import { phraseWhy } from '../services/MatchWhyPhrasingService';

const { getHeroByName } = HeroDataModule;

const Panel = styled.div`
  background: #0e1118;
  border: 1px solid #2a3346;
  border-radius: 10px;
  padding: 14px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 2px;
`;

const Sub = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 12px;
`;

const BasisNote = styled.div`
  font-size: 11px;
  color: ${(p) => (p.$overall ? '#fbbf24' : '#1de9b6')};
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border: 1px solid #20283a;
  border-radius: 8px;
  margin-bottom: 6px;
  background: ${(p) => (p.$top ? 'rgba(29,233,182,0.06)' : 'transparent')};
`;

const Thumb = styled.div`
  width: 48px;
  height: 36px;
  border-radius: 5px;
  overflow: hidden;
  flex: 0 0 auto;
  background: #0b0d14;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
`;

const Stat = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

const FreqBarOuter = styled.div`
  height: 5px;
  background: #1b2233;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
`;

const FreqBarInner = styled.div`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: linear-gradient(90deg, #1de9b6, #28b6a0);
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  color: #64748b;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
  &:hover { color: #94a3b8; }
`;

const PlaceBtn = styled.button`
  flex: 0 0 auto;
  background: ${(p) => (p.$ban ? '#ef5350' : '#1de9b6')};
  color: ${(p) => (p.$ban ? '#2a0a0a' : '#08221c')};
  font-weight: 700;
  border: none;
  border-radius: 7px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  &:hover { filter: brightness(1.12); }
`;

const Samples = styled.div`
  margin: 2px 0 8px 58px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SampleRow = styled.div`
  font-size: 11px;
  color: #8b97a8;
`;

const SampleLine = styled.button`
  all: unset;
  cursor: pointer;
  display: block;
  width: 100%;
  &:hover { color: #cbd5e1; }
`;

const WhyPanel = styled.div`
  margin: 4px 0 6px 0;
  padding: 8px 10px;
  background: rgba(167, 139, 250, 0.06);
  border: 1px solid rgba(167, 139, 250, 0.25);
  border-radius: 6px;
  font-size: 11px;
  color: #b8b3d8;
  line-height: 1.5;
`;

const WhyBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
`;

const WhyBadge = styled.span`
  font-family: 'SF Mono', ui-monospace, Menlo, Consolas, monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #a78bfa;
  background: rgba(167, 139, 250, 0.15);
  border-radius: 4px;
  padding: 2px 6px;
`;

const WhyBlurb = styled.div`
  color: #c9c3e8;
  font-style: italic;
`;

const WhyRawFacts = styled.div`
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(167, 139, 250, 0.15);
`;

const WhyFact = styled.div`
  color: #8b97a8;
  font-style: normal;
  &::before { content: "· "; color: #8b7cc7; }
`;

const WhyEmpty = styled.div`
  color: #6b7280;
  font-style: italic;
`;

const NoData = styled.div`
  text-align: center;
  padding: 16px 8px;
  color: #ef9a9a;
  font-weight: 600;
`;

const NoDataSub = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-weight: 400;
  margin-top: 4px;
`;

const YourTurn = styled.div`
  text-align: center;
  padding: 18px 8px;
  color: #ef9a9a;
  font-weight: 600;
`;
const YourTurnSub = styled.div`
  font-size: 12px; color: #94a3b8; font-weight: 400; margin-top: 4px;
`;

const Spinner = styled.div`
  width: 22px; height: 22px; margin: 16px auto;
  border: 3px solid rgba(255,255,255,0.2);
  border-top-color: #1de9b6; border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

function CandidateRow({ c, top, verb, onPlace }) {
  const [open, setOpen] = useState(false);
  const hero = getHeroByName(c.hero) || { name: c.hero };
  return (
    <>
      <Row $top={top}>
        <Thumb><HeroImage hero={hero} /></Thumb>
        <Info>
          <HeroName>{c.hero}</HeroName>
          <Stat>{c.count}/{c.total} similar games · {c.pct}% · {c.winRate}% WR</Stat>
          <FreqBarOuter><FreqBarInner $pct={c.pct} /></FreqBarOuter>
          <LinkBtn onClick={() => setOpen((o) => !o)}>
            {open ? 'hide games' : `${c.sampleMatches.length} game${c.sampleMatches.length > 1 ? 's' : ''}`}
          </LinkBtn>
        </Info>
        {onPlace && (
          <PlaceBtn $ban={verb === 'Ban'} onClick={() => onPlace(c.hero)}>{verb}</PlaceBtn>
        )}
      </Row>
      {open && (
        <Samples>
          {c.sampleMatches.map((m) => (
            <SampleMatchRow key={m.matchId} m={m} />
          ))}
        </Samples>
      )}
    </>
  );
}

function formatFacts(insight) {
  if (!insight) return [];
  const facts = [];
  if (insight.lane) {
    facts.push(`Gold at 5:00: ${insight.lane.goldAt5}${insight.lane.xpAt5 != null ? `, XP: ${insight.lane.xpAt5}` : ''}`);
  }
  if (insight.firstFight) {
    const f = insight.firstFight;
    const mm = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    const outcome = f.heroWon == null ? 'even trade' : f.heroWon ? 'won' : 'lost';
    facts.push(`First teamfight at ${mm(f.startS)} (${f.deaths} deaths) — ${outcome}`);
  }
  if (insight.followUpObjective) {
    const o = insight.followUpObjective;
    const mm = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    facts.push(`Converted into a ${o.type} at ${mm(o.time)}`);
  }
  return facts;
}

function SampleMatchRow({ m }) {
  const [expanded, setExpanded] = useState(false);
  const [insight, setInsight] = useState(undefined); // undefined=not loaded, null=loaded-empty
  const [loading, setLoading] = useState(false);
  const [blurb, setBlurb] = useState(undefined); // undefined=not requested, null=unavailable
  const [blurbLoading, setBlurbLoading] = useState(false);

  const toggle = async () => {
    setExpanded((e) => !e);
    if (insight !== undefined || !m.slot) return;
    setLoading(true);
    const match = await getMatch(m.matchId);
    const result = match ? extractPickInsight(match, m.slot) : null;
    setInsight(result);
    setLoading(false);

    const factList = formatFacts(result);
    if (factList.length > 0) {
      setBlurbLoading(true);
      const phrased = await phraseWhy(factList);
      setBlurb(phrased);
      setBlurbLoading(false);
    }
  };

  const facts = formatFacts(insight);

  return (
    <SampleRow>
      <SampleLine onClick={toggle}>
        {expanded ? '▾' : '▸'} vs {m.opponent || '?'} · {m.date || '?'} · overlap {m.overlap} · {m.won ? 'W' : 'L'}
      </SampleLine>
      {expanded && (
        <WhyPanel>
          {loading ? (
            'Loading match detail…'
          ) : !m.slot ? (
            <WhyEmpty>No draft-slot data for this game.</WhyEmpty>
          ) : facts.length === 0 ? (
            <WhyEmpty>No detail available for this game (unparsed or missing data).</WhyEmpty>
          ) : (
            <>
              <WhyBadgeRow>
                <WhyBadge>✦ AI</WhyBadge>
              </WhyBadgeRow>
              {blurbLoading ? (
                <WhyBlurb>Summarizing…</WhyBlurb>
              ) : blurb ? (
                <WhyBlurb>{blurb}</WhyBlurb>
              ) : (
                <WhyEmpty>Summary unavailable — see facts below.</WhyEmpty>
              )}
              <WhyRawFacts>
                {facts.map((f, i) => <WhyFact key={i}>{f}</WhyFact>)}
              </WhyRawFacts>
            </>
          )}
        </WhyPanel>
      )}
    </SampleRow>
  );
}

/**
 * Read-only intel for the team's upcoming slot — you place heroes on the board
 * itself, this panel only shows what the team historically did.
 * prediction: undefined ⇒ loading, null ⇒ no data, object ⇒ ranked candidates.
 */
export default function ScoutSuggestionPanel({ teamName, activeMeta, owner, prediction, loading, onPlace }) {
  if (!activeMeta) {
    return (
      <Panel>
        <Title>Draft complete</Title>
        <Sub>All 24 slots are filled. Reset the board to scout another draft.</Sub>
      </Panel>
    );
  }

  const verb = activeMeta.type === 'ban' ? 'Ban' : 'Pick';
  const ordinal = `${verb.toLowerCase()} #${activeMeta.k}`;

  if (owner === 'you') {
    return (
      <Panel>
        <Title>Your move — {verb} #{activeMeta.k} (slot {activeMeta.slot})</Title>
        <YourTurn>
          Your turn
          <YourTurnSub>Place your {verb.toLowerCase()} on the board. {teamName}'s prediction returns on their next slot.</YourTurnSub>
        </YourTurn>
      </Panel>
    );
  }

  return (
    <Panel>
      <Title>{teamName}'s move — {ordinal} (slot {activeMeta.slot})</Title>
      <Sub>What this team historically did here, ranked by how similar past drafts are to the board. Place it yourself on the board.</Sub>

      {loading ? (
        <Spinner />
      ) : prediction === null ? (
        <NoData>
          No data
          <NoDataSub>{teamName} has no historical {verb.toLowerCase()} for this slot in the selected window — no guess is offered.</NoDataSub>
        </NoData>
      ) : (
        <>
          <BasisNote $overall={prediction.basis === 'overall'}>
            {prediction.basis === 'overall'
              ? `No board overlap yet — showing ${teamName}'s overall tendency (${prediction.sampleSize} games).`
              : `Ranked from ${prediction.sampleSize} most-similar past drafts (of ${prediction.poolSize} ${teamName} games).`}
          </BasisNote>
          {prediction.candidates.slice(0, 8).map((c, i) => (
            <CandidateRow key={c.hero} c={c} top={i === 0} verb={verb} onPlace={onPlace} />
          ))}
        </>
      )}
    </Panel>
  );
}
